import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import '../styles/components/legal-pages.scss';

const TermsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <PageHeader 
        title="TERMS & CONDITIONS" 
        subtitle="Please read these terms carefully before using our service"
      />
      <div className="legal-page-container">
        <div className="legal-content">

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using the Northern Championship Series website (the "Service") operated by Northern Championship ApS 
            ("we", "us", or "our"), you accept and agree to be bound by the terms and provision of this agreement. 
            If you do not agree to abide by the above, please do not use this Service.
          </p>
        </section>

        <section>
          <h2>2. Service Description</h2>
          <p>
            Northern Championship Series provides an esports tournament platform for Counter-Strike competitions in Denmark. 
            Our services include:
          </p>
          <ul>
            <li>Tournament organization and management</li>
            <li>Player and team registration systems</li>
            <li>Match statistics and results tracking</li>
            <li>Live streaming of tournament matches</li>
            <li>Community features and social integration</li>
            <li>LAN event organization</li>
          </ul>
        </section>

        <section>
          <h2>3. User Accounts</h2>
          <h3>3.1 Account Creation</h3>
          <p>
            To participate in tournaments or access certain features, you must create an account using your Steam credentials. 
            You are responsible for maintaining the confidentiality of your account and for all activities that occur under your account.
          </p>
          
          <h3>3.2 Eligibility</h3>
          <p>
            You must be at least 16 years old to create an account. For participants under 18, parental consent is required 
            to participate in tournaments and events.
          </p>

          <h3>3.3 Account Accuracy</h3>
          <p>
            You agree to provide accurate, current, and complete information during registration and to update such information 
            to keep it accurate, current, and complete.
          </p>
        </section>

        <section>
          <h2>4. Tournament Participation</h2>
          <h3>4.1 Fair Play</h3>
          <p>
            All participants must adhere to fair play principles. Any form of cheating, including but not limited to:
          </p>
          <ul>
            <li>Use of unauthorized software or hardware</li>
            <li>Exploiting game bugs</li>
            <li>Match fixing or deliberate underperformance</li>
            <li>Account sharing during tournaments</li>
          </ul>
          <p>will result in immediate disqualification and potential permanent ban from all future events.</p>

          <h3>4.2 Code of Conduct</h3>
          <p>
            Participants must maintain professional behavior at all times. Harassment, hate speech, threats, or discrimination 
            of any kind will not be tolerated.
          </p>

          <h3>4.3 Prize Distribution</h3>
          <p>
            Tournament prizes will be distributed according to the specific tournament rules. Prize money is subject to 
            Danish tax regulations, and winners are responsible for their tax obligations.
          </p>
        </section>

        <section>
          <h2>5. Content and Intellectual Property</h2>
          <h3>5.1 User Content</h3>
          <p>
            By submitting content (including team logos, player profiles, etc.), you grant us a non-exclusive, worldwide, 
            royalty-free license to use, reproduce, and distribute such content in connection with our Service.
          </p>

          <h3>5.2 Our Content</h3>
          <p>
            All content on this website, including text, graphics, logos, and software, is the property of Northern Championship ApS 
            or its content suppliers and is protected by Danish and international copyright laws.
          </p>

          <h3>5.3 Streaming Rights</h3>
          <p>
            We reserve the exclusive right to stream and broadcast all official tournament matches. Participants may stream 
            their own perspective with appropriate delay as specified in tournament rules.
          </p>
        </section>

        <section>
          <h2>6. Data Processing</h2>
          <p>
            Your use of our Service is also governed by our <Link to="/privacy">Privacy Policy</Link> and <Link to="/cookies">Cookie Policy</Link>. 
            By using our Service, you consent to the processing of your data as described in these policies.
          </p>
        </section>

        <section>
          <h2>7. Discord Integration</h2>
          <p>
            Participation in our Discord server is subject to Discord's Terms of Service and our community guidelines. 
            We may collect and process Discord-related data for tournament administration and community management purposes.
          </p>
        </section>

        <section>
          <h2>8. Third-Party Services</h2>
          <p>
            Our Service integrates with various third-party services including:
          </p>
          <ul>
            <li>Steam (for authentication and game data)</li>
            <li>PowerStats (for match statistics)</li>
            <li>Discord (for community features)</li>
            <li>Streaming platforms (Twitch, YouTube)</li>
          </ul>
          <p>
            Your use of these services is subject to their respective terms and conditions.
          </p>
        </section>

        <section>
          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by Danish law, Northern Championship ApS shall not be liable for any indirect, 
            incidental, special, consequential, or punitive damages resulting from your use or inability to use the Service.
          </p>
        </section>

        <section>
          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Northern Championship ApS, its officers, directors, employees, and agents 
            from any claims, damages, or expenses arising from your violation of these Terms.
          </p>
        </section>

        <section>
          <h2>11. Modifications</h2>
          <p>
            We reserve the right to modify these terms at any time. We will notify users of any material changes via email 
            or through the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated terms.
          </p>
        </section>

        <section>
          <h2>12. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, 
            for any reason, including breach of these Terms.
          </p>
        </section>

        <section>
          <h2>13. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of Denmark. Any disputes arising from 
            these Terms shall be subject to the exclusive jurisdiction of the Danish courts.
          </p>
        </section>

        <section>
          <h2>14. Contact Information</h2>
          <p>
            For questions about these Terms, please contact us at: <a href="mailto:admin@ncseries.gg">admin@ncseries.gg</a>
          </p>
        </section>
        
        <p className="legal-date">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;